import DownloadIcon from '@mui/icons-material/GetApp'
import { useCallback, useState } from 'react'
import type { DataProvider } from 'react-admin'
import { Button, downloadCSV, useDataProvider, useListContext, useNotify, useTranslate } from 'react-admin'

import { sortRecords, toCsv } from '../../domain/activeInspectionExport'
import { buildHistoricalInspectionExport } from '../../domain/historicalInspectionExport'
import type { HistoricalInspection } from '../../models/inspection'

const RESOURCE = 'historical_inspections'

/** Name of the downloaded file, before `.csv`: the resource, as React Admin's own export names it. */
const EXPORT_FILENAME = RESOURCE

/**
 * Rows fetched per request. PostgREST caps a response at the project's
 * `max-rows`, which Supabase ships at 1000, so a larger page would come back
 * short.
 */
export const EXPORT_PAGE_SIZE = 1000

/**
 * Every archived inspection matching the filter, however many pages it spans.
 *
 * Paged by `id` rather than by the table's sort: the data provider orders on a
 * single field, and offset paging over a field with ties — many inspections
 * share a `day` — can repeat or skip rows across page boundaries. The id is
 * unique, so every row sits on exactly one page.
 */
export const fetchAllHistoricalInspections = async (
  dataProvider: DataProvider,
  filter: Record<string, unknown>,
): Promise<HistoricalInspection[]> => {
  const records: HistoricalInspection[] = []

  for (let page = 1; ; page++) {
    const { data, total } = await dataProvider.getList<HistoricalInspection>(RESOURCE, {
      filter,
      pagination: { page, perPage: EXPORT_PAGE_SIZE },
      sort: { field: 'id', order: 'ASC' },
    })
    records.push(...data)

    if (data.length === 0 || total === undefined || records.length >= total) {
      return records
    }

    // A short page before the end means the server capped it below the page
    // size: the next offset would skip rows, and a file missing rows would
    // give wrong counts. Better no file than a wrong one.
    if (data.length < EXPORT_PAGE_SIZE) {
      throw new Error(`export page ${String(page)} returned ${String(data.length)} of ${String(EXPORT_PAGE_SIZE)} rows before the end`)
    }
  }
}

/**
 * Exportar for the Inspecciones table: one row per archived inspection matching
 * the current filters, in the table's current order, with the columns the file
 * is read for (see `buildHistoricalInspectionExport`).
 *
 * Replaces React Admin's `ExportButton`, which writes the raw record and stops
 * at 1000 rows.
 */
export const HistoricalInspectionExportButton = () => {
  const { sort, total, ...listContext } = useListContext<HistoricalInspection>()
  // React Admin types the filter values as `any`; the data provider reads them as a plain record.
  const filterValues = listContext.filterValues as Record<string, unknown>
  const dataProvider = useDataProvider()
  const translate = useTranslate()
  const notify = useNotify()
  const [isExporting, setIsExporting] = useState(false)

  const handleExport = useCallback(async () => {
    setIsExporting(true)
    try {
      const records = await fetchAllHistoricalInspections(dataProvider, filterValues)
      downloadCSV(toCsv(buildHistoricalInspectionExport(sortRecords(records, sort), translate)), EXPORT_FILENAME)
    } catch {
      notify('ra.notification.http_error', { type: 'error' })
    } finally {
      setIsExporting(false)
    }
  }, [dataProvider, filterValues, sort, translate, notify])

  return (
    <Button
      label="ra.action.export"
      disabled={isExporting || total === 0}
      onClick={() => {
        void handleExport()
      }}
    >
      <DownloadIcon />
    </Button>
  )
}
